import Features from '@/components/Features';
import DownloadApp from '@/components/DownloadApp';
import {
  BarChartOutlined,
  DollarOutlined,
  FireOutlined,
  LikeOutlined,
} from '@/components/Icons';
import Testimonials from '@/components/Testimonials';
import { CDN2 } from '@/helpers/api';
import Head from 'next/head';
import Image from 'next/image';
import React, { Suspense } from 'react';
import Search from '@/components/Search';
import { GetServerSideProps } from 'next';
import ModalDownloadApp from '@/components2/ModalDownloadApp';
import { basicPageProps, mobileProps } from '@/utils/isServer';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { isMobile, isIos } = mobileProps(ctx);
  const { airports, configInfo } = await basicPageProps(ctx);

  return {
    props: {
      airports,
      deviceType: isMobile ? 'mobile' : 'desktop',
      mobileType: isIos ? 'ios' : 'android',
      mobileFeeDiscount: configInfo.mobileFeeDiscount || null,
    },
  };
};

export default function City({ deviceType, mobileType, mobileFeeDiscount }) {
  return (
    <div>
      <Head>
        <title>Cheap City Parking | Discount Rates at Major Cities</title>
        <meta
          name="description"
          content="Find cheapest city parking through On Air Parking. We guarantee the lowest rates with the most professional facilities. Free extensions and cancellations are always included. Book now!"
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/city"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Cheap City Parking | Discount Rates at Major Cities<"
          key="og:title"
        />
        <meta
          property="og:description"
          content="Find cheapest city parking through On Air Parking."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
      </Head>
      <Suspense>
        <div className="block sm:block md:min-h-full relative">
          <Image
            src={CDN2() + '/staticmyapp/bg-search-city.svg'}
            fill
            style={{ objectFit: 'cover' }}
            quality={100}
            className="z-0"
            alt="City Background"
          />
          <div className="container mx-auto px-4 py-8 z-10 relative">
            <h2 className="mb-2 text-2xl px-5 uppercase flex flex-row items-center w-full">
              <Image
                src={CDN2() + '/staticmyapp/icon-marker-header.svg'}
                width="17"
                height="25"
                priority
                alt="Marker"
              />{' '}
              <span className="ml-1">City</span>
            </h2>
            <Search searchType="city" deviceType={deviceType} />
          </div>
        </div>
        <DownloadApp mobileFeeDiscount={mobileFeeDiscount} />
      </Suspense>
      <Suspense>
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl text-center py-16 px-4 table mx-auto font-medium">
            Best Discounted City Parking Deals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24 mb-8">
            <div className="flex flex-row">
              <BarChartOutlined className="text-primary font-bold text-5xl mr-8" />

              <div className="text-base">
                City parking rates are on the rise but not here at On Air
                Parking — we have the cheapest parking rates in your city!
              </div>
            </div>
            <div className="flex flex-row">
              <DollarOutlined className="text-primary font-bold text-5xl mr-8" />

              <div className="text-base">
                We bust those meters and give you the cheapest daily rates for
                parking in the city.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24">
            <div className="flex flex-row">
              <LikeOutlined className="text-primary font-bold text-5xl mr-8" />

              <div className="text-base self-center">
                24/7 Facilities with Free Cancellations.
              </div>
            </div>

            <div className="flex flex-row">
              <FireOutlined className="text-primary font-bold text-5xl mr-8" />

              <div className="text-base self-center">
                Reserve a 100% guaranteed parking spot!
              </div>
            </div>
          </div>
        </div>
      </Suspense>
      <Suspense>
        <Features full={true} top />
        <Testimonials extraClasses="my-24 px-4 md:px-0" />
      </Suspense>
    </div>
  );
}
